import { type ReactNode } from 'react';
import { cn } from '@/lib/utils.ts';
import { Description, SectionTitle } from '@/components/ui/Title.tsx';
import { Skeleton } from '@/components/ui/Skeleton.tsx';

type SectionLayoutProps = {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>;

const SectionLayout = ({
  title,
  description,
  actions,
  children,
  className,
  ...props
}: SectionLayoutProps) => (
  <div className={cn('flex flex-col gap-6 lg:flex-row', className)} {...props}>
    <div className="flex w-full flex-col gap-[7px] lg:w-[250px] lg:min-w-[210px] lg:shrink-0">
      <div className="flex w-full flex-row items-start gap-2">
        <SectionTitle>{title}</SectionTitle>
        {actions}
      </div>
      {description && (
        <Description className="text-[13px] leading-[1.5] text-neutral-600">
          {description}
        </Description>
      )}
    </div>
    <div className="min-w-0 flex-1 lg:max-w-[680px]">{children}</div>
  </div>
);
SectionLayout.displayName = 'SectionLayout';

interface SectionLayoutSkeletonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  rows?: number;
  showAction?: boolean;
}

const SectionLayoutSkeleton = ({
  rows = 3,
  showAction = false,
  className,
  ...props
}: SectionLayoutSkeletonProps) => (
  <div className={cn('flex flex-col gap-6 lg:flex-row', className)} {...props}>
    <div className="flex w-full flex-col gap-[7px] lg:w-[250px] lg:min-w-[210px] lg:shrink-0">
      <div className="flex w-full flex-row gap-2">
        <Skeleton className="h-6 w-32" />
        {showAction && <Skeleton className="h-8 w-20 rounded" />}
      </div>
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-4 w-44" />
    </div>
    <div className="min-w-0 flex-1 space-y-3 lg:max-w-[680px]">
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} className="h-11 w-full rounded" />
      ))}
    </div>
  </div>
);
SectionLayoutSkeleton.displayName = 'SectionLayoutSkeleton';

export { SectionLayout, SectionLayoutSkeleton };
